import rootStore from '../store'
import { loadLanguageAsync } from '@vue-storefront/i18n'
import { Logger } from '@vue-storefront/core/lib/logger'
import Vue from 'vue'
import queryString from 'query-string'
import merge from 'lodash-es/merge'
import VueRouter, { RouteConfig, RawLocation } from 'vue-router'
import config from 'config'
import { coreHooksExecutors } from '@vue-storefront/core/hooks'
import { LocalizedRoute, StoreView } from './types'
import storeCodeFromRoute from './storeCodeFromRoute'
import cloneDeep from 'lodash-es/cloneDeep'
import get from 'lodash-es/get'
import { isServer } from '@vue-storefront/core/helpers/isServer'

function getExtendedStoreviewConfig (storeView: StoreView): StoreView {
  if (storeView.extend) {
    const originalParent = storeView.extend

    if (!config.storeViews[originalParent]) {
      Logger.error(`Storeview "${storeView.extend}" doesn't exist!`)()
    } else {
      storeView = merge(
        {},
        getExtendedStoreviewConfig(config.storeViews[originalParent]),
        storeView
      )
    }
  }

  return storeView
}

function defaultStoreId (): number {
  const code = config.defaultStoreCode
  if (!code || !config.storeViews || !config.storeViews[code]) {
    return 1
  }
  const id = config.storeViews[code].storeId
  return id != null ? id : 1
}

/**
 * Returns base storeView object that can be created without storeCode
 */
function buildBaseStoreView (): StoreView {
  return cloneDeep({
    tax: config.tax,
    i18n: config.i18n,
    elasticsearch: config.elasticsearch,
    storeCode: null,
    storeId: defaultStoreId(),
    seo: config.seo
  })
}

/**
 * Store view of the SSR request that is rendering right now.
 * vue-server-renderer evaluates the bundle in a fresh VM per request, so this
 * binding is request-scoped. Do not store it on `process`: that object is shared
 * by every sandbox, and a concurrent render then swaps the active shop mid-request.
 */
declare let __VUE_SSR_CONTEXT__: { __storeView?: StoreView } | undefined

let requestStoreView: StoreView | undefined

function bindRequestStoreView (storeView: StoreView): void {
  requestStoreView = storeView
  if (typeof __VUE_SSR_CONTEXT__ !== 'undefined' && __VUE_SSR_CONTEXT__) {
    __VUE_SSR_CONTEXT__.__storeView = storeView
  }
}

export function currentStoreView (): StoreView {
  if (!isServer) {
    return get(rootStore, 'state.storeView', undefined) || buildBaseStoreView()
  }
  if (typeof __VUE_SSR_CONTEXT__ !== 'undefined' && __VUE_SSR_CONTEXT__ && __VUE_SSR_CONTEXT__.__storeView) {
    return __VUE_SSR_CONTEXT__.__storeView
  }
  return requestStoreView || buildBaseStoreView()
}

const multistoreEnabled = (): boolean =>
  !!(config.storeViews && config.storeViews.multistore === true)

/** When `defaultStoreCode` is missing from merged client config, still pick a primary storefront if defined. */
function fallbackStoreCodeFromConfig (): string {
  const views = config.storeViews as Record<string, unknown> | undefined
  if (!views) return ''
  if (views.zaun24_de) return 'zaun24_de'
  const mapped = views.mapStoreUrlsFor as string[] | undefined
  if (Array.isArray(mapped) && mapped.length > 0 && views[mapped[0]]) {
    return mapped[0]
  }
  return ''
}

/**
 * Store code for `createApp` / SSR when the request may carry `x-vs-store-code` (e.g. Varnish).
 * If `storeViews.multistore` is false, always use `defaultStoreCode` so a wrong edge header
 * (often still set to `zaun24_at`) does not override the single storefront.
 */
export function initialStoreCodeFromRequest (req: { header?: (n: string) => string | undefined, get?: (n: string) => string | undefined }): string {
  const def = typeof config.defaultStoreCode === 'string' ? config.defaultStoreCode.trim() : ''
  if (!multistoreEnabled()) {
    const canonical = def || fallbackStoreCodeFromConfig()
    if (canonical) return canonical
  }
  const fromHeader =
    (typeof req.header === 'function' && req.header('x-vs-store-code')) ||
    (typeof req.get === 'function' && req.get('x-vs-store-code')) ||
    ''
  const fromEnv =
    typeof process !== 'undefined' && process.env && typeof process.env.STORE_CODE === 'string'
      ? process.env.STORE_CODE.trim()
      : ''
  return String(fromHeader || fromEnv || '').trim() || def || fallbackStoreCodeFromConfig() || ''
}

/**
 * Store code for the active store view: explicit view code, or
 * `config.defaultStoreCode` when the view is not set yet (e.g. early router guard before prepareStoreView).
 * When multistore is off, always returns `defaultStoreCode` if set (canonical single-store code).
 */
export function storeCode (): string {
  const def = typeof config.defaultStoreCode === 'string' ? config.defaultStoreCode.trim() : ''
  if (!multistoreEnabled()) {
    const canonical = def || fallbackStoreCodeFromConfig()
    if (canonical) return canonical
  }
  const sv = currentStoreView() as StoreView & { storeCode?: string | null }
  const code = sv && sv.storeCode
  if (typeof code === 'string' && code.length > 0) {
    return code
  }
  if (def) {
    return def
  }
  return fallbackStoreCodeFromConfig()
}

export async function prepareStoreView (storeCode: string): Promise<StoreView> {
  let storeView: StoreView = buildBaseStoreView() // current, default store
  const explicit = storeCode && String(storeCode).trim()
  const fromDefault = typeof config.defaultStoreCode === 'string' ? config.defaultStoreCode.trim() : ''
  const resolved = explicit || fromDefault || fallbackStoreCodeFromConfig() || ''
  storeView.storeCode = resolved

  const resolvedForChange = resolved

  const storeViewHasChanged = !rootStore.state.storeView || rootStore.state.storeView.storeCode !== resolvedForChange

  const hasExplicitStore = !!explicit
  const usedDefaultStoreFallback = !hasExplicitStore &&
    typeof config.defaultStoreCode === 'string' &&
    config.defaultStoreCode.trim().length > 0

  if (storeView.storeCode && config.storeViews[storeView.storeCode]) {
    storeView = merge(storeView, getExtendedStoreviewConfig(config.storeViews[storeView.storeCode]))
  }

  storeView.usedDefaultStoreFallback = usedDefaultStoreFallback
  if (isServer) bindRequestStoreView(storeView)

  if (rootStore.state.user) {
    rootStore.state.user.current_storecode = storeView.storeCode
  }

  if (storeViewHasChanged) {
    storeView = coreHooksExecutors.beforeStoreViewChanged(storeView)
    rootStore.state.storeView = storeView
    if (isServer) bindRequestStoreView(storeView)

    await loadLanguageAsync(storeView.i18n.defaultLocale)
  } else if (rootStore.state.storeView) {
    Vue.set(rootStore.state.storeView, 'usedDefaultStoreFallback', usedDefaultStoreFallback)
  }
  const { StorageManager } = require('./storage-manager')
  if (storeViewHasChanged || StorageManager.currentStoreCode !== resolved) {
    // Lazy require avoids a circular load: sync/task → helpers → url → app → multistore (before `get` inits).
    const { initializeSyncTaskStorage } = require('./sync/task')
    initializeSyncTaskStorage()
    StorageManager.currentStoreCode = storeView.storeCode
  }

  if (isServer) bindRequestStoreView(storeView)

  coreHooksExecutors.afterStoreViewChanged(storeView)

  return storeView
}

export function removeStoreCodeFromRoute (matchedRouteOrUrl: LocalizedRoute | string): LocalizedRoute | string {
  const storeCodeInRoute = storeCodeFromRoute(matchedRouteOrUrl)
  if (storeCodeInRoute !== '') {
    const urlPath = typeof matchedRouteOrUrl === 'object' ? matchedRouteOrUrl.path : matchedRouteOrUrl
    return urlPath.replace(storeCodeInRoute + '/', '')
  } else {
    return matchedRouteOrUrl
  }
}

function removeURLQueryParameter (url, parameter) {
  // prefer to use l.search if you have a location/link object
  const urlparts = url.split('?');
  if (urlparts.length >= 2) {
    const prefix = encodeURIComponent(parameter) + '=';
    const pars = urlparts[1].split(/[&;]/g);

    // reverse iteration as may be destructive
    for (let i = pars.length; i-- > 0;) {
      // idiom for string.startsWith
      if (pars[i].lastIndexOf(prefix, 0) !== -1) {
        pars.splice(i, 1);
      }
    }

    return urlparts[0] + (pars.length > 0 ? '?' + pars.join('&') : '');
  }
  return url;
}

export function adjustMultistoreApiUrl (url: string): string {
  const { storeCode } = currentStoreView()
  if (storeCode) {
    url = removeURLQueryParameter(url, 'storeCode')
    const urlSep = (url.indexOf('?') > 0) ? '&' : '?'
    url += `${urlSep}storeCode=${storeCode}`
  }
  return url
}

export function localizedDispatcherRoute (routeObj: LocalizedRoute | string, storeCode?: string): LocalizedRoute | string {
  const { storeCode: currentStoreCode, appendStoreCode } = currentStoreView()
  if (!storeCode || !config.storeViews[storeCode]) {
    storeCode = currentStoreCode
  }
  const appendStoreCodePrefix = storeCode && appendStoreCode

  if (typeof routeObj === 'string') {
    if (routeObj[0] !== '/') routeObj = `/${routeObj}`
    return appendStoreCodePrefix ? `/${storeCode}${routeObj}` : routeObj
  }

  if (routeObj) {
    if ((routeObj as LocalizedRoute).fullPath && !(routeObj as LocalizedRoute).path) { // support both path and fullPath
      routeObj.path = (routeObj as LocalizedRoute).fullPath
    }

    if (routeObj.path) { // case of using dispatcher
      const routeCodePrefix = appendStoreCodePrefix ? `/${storeCode}` : ''
      const qrStr = queryString.stringify(routeObj.params);

      const normalizedPath = routeObj.path[0] !== '/' ? `/${routeObj.path}` : routeObj.path
      return `${routeCodePrefix}${normalizedPath}${qrStr ? `?${qrStr}` : ''}`
    }
  }

  return routeObj
}

export function localizedDispatcherRouteName (routeName: string, storeCode: string, appendStoreCode = false): string {
  if (appendStoreCode) {
    return `${storeCode}-${routeName}`
  }
  return routeName
}

/**
 * Returns route path with proper language prefix
 * @param path - route path
 * @param storeCode - language prefix specified in global config
 */
export function localizedRoutePath (path: string, storeCode: string): string {
  const _path = path.startsWith('/') ? path.slice(1) : path

  return `/${storeCode}/${_path}`
}

/**
 * Returns transformed route config with language
 * @param route - route config object
 * @param storeCode - language prefix specified in global config
 * @param isChildRoute - determines if route config is for child route
 */
export function localizedRouteConfig (route: RouteConfig, storeCode: string, isChildRoute = false): RouteConfig {
  // note: we need shallow copy to prevent modifications in provided route object
  const _route = { ...route }

  if (_route.name && storeCode) {
    _route.name = `${storeCode}-${_route.name}`
  }

  if (_route.path && !isChildRoute) {
    _route.path = localizedRoutePath(_route.path, storeCode)
  }

  if (_route.children) {
    _route.children = _route.children.map(childRoute => localizedRouteConfig(childRoute, storeCode, true))
  }

  return _route
}

export function localizedRoute (routeObj: LocalizedRoute | string | RouteConfig | RawLocation, storeCode: string = null): any {
  if (!storeCode) {
    storeCode = currentStoreView().storeCode
  }
  if (!routeObj) {
    return routeObj
  }

  if ((typeof routeObj === 'object') && (routeObj as LocalizedRoute)) {
    if ((routeObj as LocalizedRoute).fullPath && !(routeObj as LocalizedRoute).path) { // support both path and fullPath
      routeObj.path = (routeObj as LocalizedRoute).fullPath
    }
  }

  if (storeCode && config.defaultStoreCode !== storeCode && config.storeViews[storeCode] && config.storeViews[storeCode].appendStoreCode) {
    if (typeof routeObj !== 'object') {
      return localizedRoutePath(routeObj, storeCode)
    }
    return localizedRouteConfig(routeObj as RouteConfig, storeCode)
  }

  return routeObj
}

export function setupMultistoreRoutes (config, router: VueRouter, routes: RouteConfig[], priority = 0): void {
  const allRoutes: RouteConfig[] = []
  const { storeCode, appendStoreCode } = currentStoreView()
  if (storeCode && appendStoreCode) {
    allRoutes.push(...routes.map(route => localizedRouteConfig(route, storeCode)))
  } else {
    allRoutes.push(...routes)
  }
  router.addRoutes(allRoutes, true, priority)
}
